import { useId, useMemo, useState } from 'react';
import { I } from '../lib/icons';
import { Pill } from './ui/Pill';

/**
 * ProductCategoryTree — Kategorie + Produkt als SICHTBARER inline-Baum im
 * Filter-Panel (2026-06-15). Löst die `WarengruppeCombobox` (#191) ab, die beide
 * Ebenen in EIN absolutes Dropdown-Overlay versteckte: Kategorie/Produkt ist der
 * primäre Browse-Einstieg und gehört direkt sichtbar, nicht hinter einen Klick
 * (und das Overlay ragte auf Mobile über Verwendung/Tags).
 *
 * Verhalten:
 * - Such-Input oben (immer sichtbar) filtert beide Ebenen live (NFC beidseitig —
 *   manche Namen sind in der DB NFD; Mirror der Upload-Logik).
 * - Kategorien immer sichtbar (fett, wählbar → kategorie-Filter).
 * - Produkte eingerückt darunter: standardmäßig die ersten `VISIBLE_PRODUCTS`,
 *   Rest hinter „+N weitere" (per-Kategorie Expand-State). Bei aktiver Suche
 *   werden ALLE Treffer gezeigt (keine N-Kappung).
 * - Liste rendert IN-FLOW (kein `absolute`/Overlay) → schiebt die Folge-Sektionen
 *   nach unten, auch im Mobile-Bottom-Sheet.
 * - Gewählte (Kat + Produkt) als entfernbare Chips oben; in der Liste mit Häkchen
 *   (`aria-selected`) sichtbar, wenn im aktuellen Ausschnitt.
 *
 * KEINE Cascade, KEIN Backend-Change: ?kategorie= / ?produkt= (AND-across) + Counts
 * unverändert.
 */
type Kategorie = { id: string; name: string; produkte: { id: string; name: string }[] };

type Props = {
  kategorien: Kategorie[];
  selectedKategorie: string[];
  selectedProdukt: string[];
  kategorieCounts: Map<string, number>;
  produktCounts: Map<string, number>;
  showCounts: boolean;
  onKategorieToggle: (id: string) => void;
  onProduktToggle: (id: string) => void;
  /** Text in der Liste, wenn (noch) keine Kategorien geladen sind. */
  emptyHint?: string;
};

// Standard sichtbare Produkte pro Kategorie (Rest hinter „+N weitere"). Klein
// halten → der Baum bleibt kompakt, auch bei vielen Kategorien.
const VISIBLE_PRODUCTS = 4;

const LABEL_CLASS =
  'block mb-1.5 text-xs2 font-semibold uppercase tracking-[0.06em] text-brand-green-600';
const INPUT_CLASS =
  'w-full h-9 px-2.5 rounded-md text-xs bg-white border border-ink-200 text-brand-green-800 placeholder:text-brand-green-400 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500';
const CHIP_CLASS =
  'inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-brand-green-50 border border-brand-green-200 text-xs2 text-brand-green-700';

const norm = (s: string): string => s.normalize('NFC').toLowerCase();

export function ProductCategoryTree({
  kategorien,
  selectedKategorie,
  selectedProdukt,
  kategorieCounts,
  produktCounts,
  showCounts,
  onKategorieToggle,
  onProduktToggle,
  emptyHint = 'Wird geladen…',
}: Props) {
  const reactId = useId();
  const listboxId = `${reactId}-listbox`;
  const [query, setQuery] = useState('');
  // Per-Kategorie aufgeklappt (zeigt alle Produkte statt der ersten N). UNABHÄNGIG
  // von der Suche — ein Query-Clear lässt den Expand-State stehen.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Lookup-Maps für Chip-Labels (id → name), beide Ebenen.
  const katName = useMemo(() => new Map(kategorien.map((k) => [k.id, k.name])), [kategorien]);
  const prodName = useMemo(
    () => new Map(kategorien.flatMap((k) => k.produkte.map((p) => [p.id, p.name] as const))),
    [kategorien],
  );

  // Gefilterte Hierarchie: Kategorie-Name-Match → ganze Gruppe; sonst nur die
  // Produkte, deren „Kategorie Produkt"-Kombi matcht (Mirror der Upload-Logik).
  const groups = useMemo(() => {
    const q = norm(query.trim());
    const out: Kategorie[] = [];
    for (const kat of kategorien) {
      const katMatch = q === '' || norm(kat.name).includes(q);
      const produkte = katMatch
        ? kat.produkte
        : kat.produkte.filter((p) => norm(`${kat.name} ${p.name}`).includes(q));
      if (katMatch || produkte.length > 0) out.push({ ...kat, produkte });
    }
    return out;
  }, [kategorien, query]);

  const searching = query.trim() !== '';
  const selKat = new Set(selectedKategorie);
  const selProd = new Set(selectedProdukt);
  const chipCount = selectedKategorie.length + selectedProdukt.length;

  function expand(id: string): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  return (
    <div role="group" aria-label="Produkt & Kategorie">
      <span className={LABEL_CLASS}>Produkt &amp; Kategorie</span>

      {chipCount > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedKategorie.map((id) => (
            <Chip
              key={`k-${id}`}
              label={katName.get(id) ?? id}
              onRemove={() => onKategorieToggle(id)}
            />
          ))}
          {selectedProdukt.map((id) => (
            <Chip
              key={`p-${id}`}
              label={prodName.get(id) ?? id}
              onRemove={() => onProduktToggle(id)}
            />
          ))}
        </div>
      )}

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          // Esc mit Text leert nur die Suche (verbraucht das Event → das
          // umgebende Chip-Popover bleibt offen); leeres Feld → Popover schließt.
          if (e.key === 'Escape' && query) {
            e.stopPropagation();
            setQuery('');
          }
        }}
        placeholder="Kategorie oder Produkt suchen…"
        className={INPUT_CLASS}
        autoComplete="off"
        aria-label="Produkt & Kategorie suchen"
        aria-controls={listboxId}
      />

      <div
        id={listboxId}
        role="listbox"
        aria-label="Produkt & Kategorie"
        className="mt-1.5 max-h-[60vh] overflow-auto -mx-1 px-1"
      >
        {kategorien.length === 0 ? (
          <div className="px-2 py-1.5 text-xs2 text-brand-green-600">{emptyHint}</div>
        ) : groups.length === 0 ? (
          <div className="px-2 py-1.5 text-xs2 text-brand-green-600">Nichts gefunden</div>
        ) : (
          groups.map((kat) => {
            // Aufgeklappt = Suche aktiv ODER per „+N weitere" expandiert → alle
            // (gefilterten) Produkte; sonst die ersten N + „+N weitere".
            const isOpen = searching || expanded.has(kat.id);
            const visible = isOpen ? kat.produkte : kat.produkte.slice(0, VISIBLE_PRODUCTS);
            const hidden = kat.produkte.length - visible.length;
            return (
              <div key={kat.id}>
                <Row
                  label={kat.name}
                  isSelected={selKat.has(kat.id)}
                  count={kategorieCounts.get(kat.id) ?? 0}
                  showCounts={showCounts}
                  onToggle={() => onKategorieToggle(kat.id)}
                  level="kategorie"
                />
                {visible.map((p) => (
                  <Row
                    key={p.id}
                    label={p.name}
                    isSelected={selProd.has(p.id)}
                    count={produktCounts.get(p.id) ?? 0}
                    showCounts={showCounts}
                    onToggle={() => onProduktToggle(p.id)}
                    level="produkt"
                  />
                ))}
                {hidden > 0 && (
                  <button
                    type="button"
                    onClick={() => expand(kat.id)}
                    className="ml-7 mb-0.5 px-2 py-1 text-xs2 font-semibold text-brand-green-600 hover:text-brand-green-800 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700"
                  >
                    + {hidden} weitere
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// Entfernbarer Chip für eine gesetzte Kategorie oder ein gesetztes Produkt.
function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className={CHIP_CLASS}>
      <span className="truncate max-w-[10rem]">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`${label} entfernen`}
        className="text-brand-green-600 hover:text-brand-green-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
      >
        ×
      </button>
    </span>
  );
}

// Eine wählbare Zeile (Kategorie = fett/bündig, Produkt = eingerückt). Checkbox-
// Indikator + Name + optionale Count-Pill. <button role="option" aria-selected>.
// `title` auf dem truncatenden Span → voller Name bei Abschneidung lesbar.
function Row({
  label,
  isSelected,
  count,
  showCounts,
  onToggle,
  level,
}: {
  label: string;
  isSelected: boolean;
  count: number;
  showCounts: boolean;
  onToggle: () => void;
  level: 'kategorie' | 'produkt';
}) {
  const isKat = level === 'kategorie';
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      onClick={onToggle}
      className={`w-full flex items-center gap-2 py-1.5 rounded text-xs text-left hover:bg-brand-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 ${
        isKat ? 'px-2 mt-0.5' : 'pl-7 pr-2'
      }`}
    >
      <span
        aria-hidden="true"
        className={`flex items-center justify-center shrink-0 w-4 h-4 rounded border transition ${
          isSelected
            ? 'bg-brand-green-700 border-brand-green-700 text-brand-cream'
            : 'bg-white border-ink-300'
        }`}
      >
        {isSelected && <I.check size={10} />}
      </span>
      <span
        title={label}
        className={`flex-1 truncate ${
          isKat ? 'font-semibold text-brand-green-800' : 'text-brand-green-700'
        } ${isSelected ? 'text-brand-green-700' : ''}`}
      >
        {label}
      </span>
      {showCounts && count > 0 && (
        <Pill tone="neutral" aria-label={`${count} Asset(s)`}>
          {count}
        </Pill>
      )}
    </button>
  );
}
